import React, { useEffect, useMemo, useState } from "react";

import { useLocation, useNavigate } from "react-router-dom";

import Header from "../../master/SideBar";
import Nav from "../../master/NavBar";
import Master from "../Master";

import { LoadingDiv, SweetAlertShow } from "../../master/Master";

import { courseList } from "../../../api/Courses";
import { batchList } from "../../../api/Batch";
import { StaffList } from "../../../api/Staff";

import { studentAttendanceListAnnualV4 } from "../../../api/Attendance";

import { formatToDate } from "../../master/FormatToDateTime";
import MasterV2 from "../MasterV2";

/* =========================================================
   CONSTANTS
========================================================= */

const BATCH_NOT_SELECTED = "";
const ALL_BATCHES = "__all__";

/* =========================================================
   HELPERS
========================================================= */

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const getInitials = (name) =>
  String(name || "?")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

const safeFormatDate = (value) => {
  if (!value) return "";

  try {
    return formatToDate(value);
  } catch {
    return value;
  }
};

const toDate = (value) => {
  if (!value) return null;

  /*
   * First try direct JS parsing.
   */
  const direct = new Date(value);

  if (!Number.isNaN(direct.getTime())) {
    direct.setHours(0, 0, 0, 0);
    return direct;
  }

  /*
   * Try formatted value.
   */
  try {
    const formatted = formatToDate(value);

    const date = new Date(formatted);

    if (!Number.isNaN(date.getTime())) {
      date.setHours(0, 0, 0, 0);
      return date;
    }
  } catch {
    //
  }

  return null;
};

const dateKey = (value) => {
  const date = toDate(value);

  if (!date) return "";

  const year = date.getFullYear();

  const month = String(date.getMonth() + 1).padStart(2, "0");

  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const addDays = (date, amount) => {
  const copy = new Date(date);

  copy.setDate(copy.getDate() + amount);

  return copy;
};

const daysBetweenInclusive = (startDate, endDate) => {
  if (!startDate || !endDate) {
    return 0;
  }

  const start = new Date(startDate);
  const end = new Date(endDate);

  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);

  if (start > end) {
    return 0;
  }

  const diff = end.getTime() - start.getTime();

  return Math.floor(diff / (1000 * 60 * 60 * 24)) + 1;
};

const formatReadableDate = (value) => {
  const date = toDate(value);

  if (!date) return "-";

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const downloadBlob = (content, fileName, type) => {
  const blob = new Blob(["\ufeff", content], {
    type,
  });

  const url = URL.createObjectURL(blob);

  const anchor = document.createElement("a");

  anchor.href = url;
  anchor.download = fileName;

  document.body.appendChild(anchor);

  anchor.click();
  anchor.remove();

  URL.revokeObjectURL(url);
};

/* =========================================================
   TRY TO READ SESSION DATES

   Supports common session formats:
   2026-2027
   2026 - 2027
   2026/2027

   If actual start/end date fields exist in localStorage
   session data, they are preferred.
========================================================= */

const getSessionDateRange = (session) => {
  if (!session) {
    return {
      startDate: null,
      endDate: null,
      title: "",
    };
  }

  const explicitStart =
    session?.start_date ||
    session?.from_date ||
    session?.session_start_date ||
    session?.academic_start_date;

  const explicitEnd =
    session?.end_date ||
    session?.to_date ||
    session?.session_end_date ||
    session?.academic_end_date;

  if (toDate(explicitStart) && toDate(explicitEnd)) {
    return {
      startDate: toDate(explicitStart),

      endDate: toDate(explicitEnd),

      title: session?.session || "",
    };
  }

  const title = String(session?.session || "");

  const years = title.match(/\d{4}/g);

  if (years && years.length >= 2) {
    const startYear = Number(years[0]);

    const endYear = Number(years[1]);

    /*
     * Common Indian academic year:
     * 1 April -> 31 March.
     *
     * Change these two dates here if your
     * school session uses another period.
     */
    return {
      startDate: new Date(startYear, 3, 1),

      endDate: new Date(endYear, 2, 31),

      title,
    };
  }

  return {
    startDate: null,
    endDate: null,
    title,
  };
};

/* =========================================================
   COMPONENT
========================================================= */

export default function StudentAttendancesAnnualv2() {
  const navigate = useNavigate();
  const location = useLocation();

  /* =======================================================
     URL
  ======================================================= */

  const initialParams = useMemo(() => new URLSearchParams(location.search), []);

  const urlBatchId = initialParams.get("batch_id") || "";

  const urlAllBatches = initialParams.get("all_batches") === "1";

  /* =======================================================
     STATE
  ======================================================= */

  const [loading, setLoading] = useState(false);

  const [filterLoading, setFilterLoading] = useState(false);

  const [data, setData] = useState([]);

  const [courses, setCourses] = useState([]);

  const [batches, setBatches] = useState([]);

  const [staffs, setStaffs] = useState([]);

  const [sessions, setSessions] = useState([]);

  const [search, setSearch] = useState("");

  const [attendanceView, setAttendanceView] = useState("all");

  const [printMenuOpen, setPrintMenuOpen] = useState(false);

  const [filters, setFilters] = useState({
    session_id: initialParams.get("session_id") || "",

    course_id: initialParams.get("course_id") || "",

    batch_id: urlAllBatches ? ALL_BATCHES : urlBatchId || BATCH_NOT_SELECTED,

    student_type: initialParams.get("student_type") || "",

    staff_id: initialParams.get("staff_id") || "",
  });

  /* =======================================================
     BATCH STATE
  ======================================================= */

  const hasBatchSelection = filters.batch_id !== BATCH_NOT_SELECTED;

  const isAllBatches = filters.batch_id === ALL_BATCHES;

  /* =======================================================
     SESSIONS
  ======================================================= */

  useEffect(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem("sessions") ?? "[]");

      setSessions(Array.isArray(parsed) ? parsed : []);
    } catch {
      setSessions([]);
    }
  }, []);

  useEffect(() => {
    if (!sessions.length) {
      return;
    }

    if (filters.session_id) {
      return;
    }

    const active = sessions.find((session) => Number(session?.status) === 1);

    if (!active?.id) {
      return;
    }

    setFilters((previous) => ({
      ...previous,

      session_id: String(active.id),

      /*
       * IMPORTANT:
       * Don't automatically fetch annual
       * report.
       */
      batch_id: BATCH_NOT_SELECTED,
    }));
  }, [sessions, filters.session_id]);

  /* =======================================================
     SELECTED FILTER OBJECTS
  ======================================================= */

  const selectedSession = useMemo(
    () =>
      sessions.find(
        (session) => String(session?.id) === String(filters.session_id),
      ),
    [sessions, filters.session_id],
  );

  const selectedCourse = useMemo(
    () =>
      courses.find(
        (course) => String(course?.id) === String(filters.course_id),
      ),
    [courses, filters.course_id],
  );

  const selectedBatch = useMemo(
    () =>
      batches.find((batch) => String(batch?.id) === String(filters.batch_id)),
    [batches, filters.batch_id],
  );

  const selectedStaff = useMemo(
    () =>
      staffs.find((staff) => String(staff?.id) === String(filters.staff_id)),
    [staffs, filters.staff_id],
  );

  const sessionRange = useMemo(
    () => getSessionDateRange(selectedSession),
    [selectedSession],
  );

  /* =======================================================
     COURSES
  ======================================================= */

  const getCourses = async () => {
    try {
      setFilterLoading(true);

      const response = await courseList();

      if (response?.data?.status) {
        setCourses(
          Array.isArray(response?.data?.data) ? response.data.data : [],
        );
      } else {
        setCourses([]);
      }
    } catch (error) {
      console.error("Course loading error:", error);

      setCourses([]);

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
          error?.message ||
          "Unable to load courses.",
      );
    } finally {
      setFilterLoading(false);
    }
  };

  /* =======================================================
     STAFF
  ======================================================= */

  const getStaffs = async () => {
    try {
      const response = await StaffList("status=1");

      if (response?.data?.status) {
        setStaffs(
          Array.isArray(response?.data?.data) ? response.data.data : [],
        );
      } else {
        setStaffs([]);
      }
    } catch (error) {
      console.error("Staff loading error:", error);

      setStaffs([]);
    }
  };

  useEffect(() => {
    getCourses();
    getStaffs();
  }, []);

  /* =======================================================
     BATCHES
  ======================================================= */

  const getBatches = async () => {
    if (!filters.session_id) {
      setBatches([]);
      return;
    }

    try {
      setFilterLoading(true);

      let addUrl = "&status=1";

      addUrl += `&session_id=${encodeURIComponent(filters.session_id)}`;

      if (filters.course_id) {
        addUrl += `&course_id=${encodeURIComponent(filters.course_id)}`;
      }

      const response = await batchList(addUrl);

      if (response?.data?.status) {
        setBatches(
          Array.isArray(response?.data?.data) ? response.data.data : [],
        );
      } else {
        setBatches([]);
      }
    } catch (error) {
      console.error("Batch loading error:", error);

      setBatches([]);
    } finally {
      setFilterLoading(false);
    }
  };

  useEffect(() => {
    getBatches();

    // eslint-disable-next-line
  }, [filters.session_id, filters.course_id]);

  /* =======================================================
     API QUERY
  ======================================================= */

  const buildQuery = () => {
    const queryFilters = {
      session_id: filters.session_id,

      course_id: filters.course_id,

      student_type: filters.student_type,

      staff_id: filters.staff_id,
    };

    /*
     * Specific batch:
     * send batch_id.
     *
     * All Batches:
     * DON'T send batch_id.
     */
    if (filters.batch_id && filters.batch_id !== ALL_BATCHES) {
      queryFilters.batch_id = filters.batch_id;
    }

    let addUrl = "";

    Object.entries(queryFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && String(value) !== "") {
        addUrl += `&${key}=${encodeURIComponent(value)}`;
      }
    });

    return addUrl;
  };

  /* =======================================================
     FETCH ANNUAL ATTENDANCE
  ======================================================= */

  const getAttendanceList = async () => {
    /*
     * DO NOT fetch annual report
     * until batch is selected.
     */
    if (!hasBatchSelection) {
      setData([]);
      return;
    }

    if (!filters.session_id) {
      setData([]);
      return;
    }

    try {
      setLoading(true);

      const addUrl = buildQuery();

      console.log("Annual Attendance Query:", addUrl);

      const response = await studentAttendanceListAnnualV4(addUrl);

      if (response?.data?.status) {
        const responseData = response?.data?.data;

        setData(Array.isArray(responseData) ? responseData : []);
      } else {
        setData([]);

        if (response?.data?.message) {
          SweetAlertShow("info", "Information", response.data.message);
        }
      }
    } catch (error) {
      console.error("Annual attendance error:", error);

      setData([]);

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
          error?.message ||
          "Unable to load annual attendance.",
      );
    } finally {
      setLoading(false);
    }
  };

  /* =======================================================
     AUTO FETCH ONLY AFTER BATCH SELECTION
  ======================================================= */

  useEffect(() => {
    if (!hasBatchSelection) {
      setData([]);
      return;
    }

    if (!filters.session_id) {
      return;
    }

    getAttendanceList();

    // eslint-disable-next-line
  }, [
    filters.session_id,
    filters.course_id,
    filters.batch_id,
    filters.student_type,
    filters.staff_id,
  ]);

  /* =======================================================
     URL SYNC
  ======================================================= */

  useEffect(() => {
    if (!filters.session_id) {
      return;
    }

    const params = new URLSearchParams();

    params.set("session_id", filters.session_id);

    if (filters.course_id) {
      params.set("course_id", filters.course_id);
    }

    if (filters.batch_id && filters.batch_id !== ALL_BATCHES) {
      params.set("batch_id", filters.batch_id);
    }

    if (filters.batch_id === ALL_BATCHES) {
      params.set("all_batches", "1");
    }

    if (filters.student_type) {
      params.set("student_type", filters.student_type);
    }

    if (filters.staff_id) {
      params.set("staff_id", filters.staff_id);
    }

    navigate(
      {
        pathname: location.pathname,

        search: `?${params.toString()}`,
      },
      {
        replace: true,
      },
    );

    // eslint-disable-next-line
  }, [
    filters.session_id,
    filters.course_id,
    filters.batch_id,
    filters.student_type,
    filters.staff_id,
  ]);

  /* =======================================================
     ALL ATTENDANCE DATE SET

     This is calculated once instead of scanning every
     student repeatedly for every date.
  ======================================================= */

  const attendanceDateSet = useMemo(() => {
    const set = new Set();

    data.forEach((student) => {
      const attendances = Array.isArray(student?.attendances)
        ? student.attendances
        : [];

      attendances.forEach((attendance) => {
        const key = dateKey(attendance?.date);

        if (key) {
          set.add(key);
        }
      });
    });

    return set;
  }, [data]);

  /* =======================================================
     REPORT END DATE

     Do not calculate future dates as attendance days.
  ======================================================= */

  const reportEndDate = useMemo(() => {
    const today = new Date();

    today.setHours(0, 0, 0, 0);

    if (sessionRange.endDate && sessionRange.endDate < today) {
      return new Date(sessionRange.endDate);
    }

    return today;
  }, [sessionRange]);

  /* =======================================================
     ANALYSE STUDENT
  ======================================================= */

  const analyseStudent = (student) => {
    const attendances = Array.isArray(student?.attendances)
      ? student.attendances
      : [];

    const admissionValue =
      student?.latest_course_and_fee?.admission_date ||
      student?.latest_course_and_fee?.created_at ||
      student?.created_at;

    let admissionDate = toDate(admissionValue);

    /*
     * If session start exists and student was
     * admitted before session start, calculations
     * start from session start.
     */
    let startDate = admissionDate;

    if (sessionRange.startDate) {
      if (!startDate || startDate < sessionRange.startDate) {
        startDate = new Date(sessionRange.startDate);
      }
    }

    /*
     * Fallback:
     * earliest attendance date.
     */
    if (!startDate) {
      const dates = attendances
        .map((item) => toDate(item?.date))
        .filter(Boolean)
        .sort((a, b) => a.getTime() - b.getTime());

      startDate = dates[0] || reportEndDate;
    }

    const endDate = reportEndDate;

    let totalDays = 0;
    let holidays = 0;
    let workingDays = 0;
    let present = 0;
    let absent = 0;
    let notMarked = 0;

    /*
     * Attendance status by date.
     */
    const studentAttendanceMap = new Map();

    attendances.forEach((attendance) => {
      const key = dateKey(attendance?.date);

      if (key) {
        studentAttendanceMap.set(key, attendance);
      }
    });

    if (startDate && endDate && startDate <= endDate) {
      totalDays = daysBetweenInclusive(startDate, endDate);

      for (
        let current = new Date(startDate);
        current <= endDate;
        current = addDays(current, 1)
      ) {
        const key = dateKey(current);

        const hasSchoolAttendance = attendanceDateSet.has(key);

        /*
         * Same concept as your old report:
         * no attendance by anyone on this day =
         * holiday.
         */
        if (!hasSchoolAttendance) {
          holidays += 1;
          continue;
        }

        workingDays += 1;

        const attendance = studentAttendanceMap.get(key);

        if (Number(attendance?.check_in) === 1) {
          present += 1;
        } else if (Number(attendance?.check_in) === 2) {
          absent += 1;
        } else {
          notMarked += 1;
        }
      }
    }

    /*
     * Safety fallback for API attendance records
     * that somehow sit outside calculated range.
     */
    if (workingDays === 0 && attendances.length > 0) {
      present = attendances.reduce(
        (sum, attendance) => sum + (Number(attendance?.check_in) === 1 ? 1 : 0),
        0,
      );

      absent = attendances.reduce(
        (sum, attendance) => sum + (Number(attendance?.check_in) === 2 ? 1 : 0),
        0,
      );

      workingDays = present + absent;

      totalDays = workingDays;

      holidays = 0;
      notMarked = 0;
    }

    const percentage =
      workingDays > 0 ? Number(((present / workingDays) * 100).toFixed(1)) : 0;

    return {
      student,

      admissionDate,

      reportStartDate: startDate,

      reportEndDate: endDate,

      totalDays,
      holidays,
      workingDays,
      present,
      absent,
      notMarked,
      percentage,
    };
  };

  const analysedRows = useMemo(
    () => data.map(analyseStudent),

    // eslint-disable-next-line
    [data, attendanceDateSet, sessionRange, reportEndDate],
  );

  /* =======================================================
     SEARCH / LOCAL FILTER
  ======================================================= */

  const filteredRows = useMemo(() => {
    const keyword = search.trim().toLowerCase();

    return analysedRows.filter((row) => {
      const student = row.student;

      const course = student?.latest_course_and_fee?.course?.title || "";

      const batch = student?.latest_course_and_fee?.batch?.title || "";

      const searchable = [
        student?.name,
        student?.phone,
        student?.mobile,
        student?.registration_no,
        student?.gender,
        course,
        batch,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      if (keyword && !searchable.includes(keyword)) {
        return false;
      }

      if (attendanceView === "low") {
        return row.workingDays > 0 && row.percentage < 75;
      }

      if (attendanceView === "good") {
        return row.workingDays > 0 && row.percentage >= 75;
      }

      if (attendanceView === "excellent") {
        return row.workingDays > 0 && row.percentage >= 90;
      }

      if (attendanceView === "absent") {
        return row.absent > 0;
      }

      if (attendanceView === "not_marked") {
        return row.notMarked > 0;
      }

      return true;
    });
  }, [analysedRows, search, attendanceView]);

  /* =======================================================
     SUMMARY
  ======================================================= */

  const summary = useMemo(() => {
    let totalPresent = 0;
    let totalAbsent = 0;
    let totalNotMarked = 0;
    let totalWorkingDays = 0;
    let lowAttendance = 0;
    let excellentAttendance = 0;

    analysedRows.forEach((row) => {
      totalPresent += row.present;

      totalAbsent += row.absent;

      totalNotMarked += row.notMarked;

      totalWorkingDays += row.workingDays;

      if (row.workingDays > 0 && row.percentage < 75) {
        lowAttendance += 1;
      }

      if (row.workingDays > 0 && row.percentage >= 90) {
        excellentAttendance += 1;
      }
    });

    const overallPercentage =
      totalWorkingDays > 0
        ? Number(((totalPresent / totalWorkingDays) * 100).toFixed(1))
        : 0;

    return {
      students: analysedRows.length,

      present: totalPresent,

      absent: totalAbsent,

      notMarked: totalNotMarked,

      workingDays: totalWorkingDays,

      lowAttendance,

      excellentAttendance,

      percentage: overallPercentage,
    };
  }, [analysedRows]);

  /* =======================================================
     HANDLERS
  ======================================================= */

  const handleSessionChange = (event) => {
    setSearch("");
    setAttendanceView("all");
    setData([]);

    setFilters((previous) => ({
      ...previous,

      session_id: event.target.value,

      course_id: "",

      /*
       * Reset batch.
       */
      batch_id: BATCH_NOT_SELECTED,
    }));
  };

  const handleCourseChange = (event) => {
    setSearch("");
    setAttendanceView("all");
    setData([]);

    setFilters((previous) => ({
      ...previous,

      course_id: event.target.value,

      batch_id: BATCH_NOT_SELECTED,
    }));
  };

  const handleBatchChange = (event) => {
    setSearch("");
    setAttendanceView("all");
    setData([]);

    setFilters((previous) => ({
      ...previous,

      batch_id: event.target.value,
    }));
  };

  const resetFilters = () => {
    const active = sessions.find((session) => Number(session?.status) === 1);

    setSearch("");
    setAttendanceView("all");
    setData([]);

    setFilters({
      session_id: active?.id ? String(active.id) : "",

      course_id: "",

      batch_id: BATCH_NOT_SELECTED,

      student_type: "",

      staff_id: "",
    });
  };

  /* =======================================================
     PRINT
  ======================================================= */

  const getPrintMetaHtml = () => `
      <div class="meta-grid">

        <div>
          <span>Academic Session</span>

          <strong>
            ${escapeHtml(selectedSession?.session || "-")}
          </strong>
        </div>

        <div>
          <span>Course</span>

          <strong>
            ${escapeHtml(selectedCourse?.title || "All Courses")}
          </strong>
        </div>

        <div>
          <span>Batch</span>

          <strong>
            ${escapeHtml(
              isAllBatches ? "All Batches" : selectedBatch?.title || "-",
            )}
          </strong>
        </div>

        <div>
          <span>Class Teacher</span>

          <strong>
            ${escapeHtml(selectedStaff?.name || "All Teachers")}
          </strong>
        </div>

        <div>
          <span>Student Type</span>

          <strong>
            ${escapeHtml(
              filters.student_type === "hostel"
                ? "Hostel Students"
                : "All Students",
            )}
          </strong>
        </div>

      </div>
    `;

  const openPrintWindow = (title, content) => {
    const printWindow = window.open("", "_blank", "width=1450,height=900");

    if (!printWindow) {
      SweetAlertShow(
        "info",
        "Popup Blocked",
        "Please allow popups for this website and try again.",
      );

      return;
    }

    printWindow.document.open();

    printWindow.document.write(`
      <!DOCTYPE html>

      <html>

      <head>

        <meta charset="UTF-8" />

        <title>
          ${escapeHtml(title)}
        </title>

        <style>

          @page {
            size: A4 landscape;
            margin: 9mm;
          }

          * {
            box-sizing: border-box;
          }

          body {
            margin: 0;

            color: #262b34;

            font-family:
              Arial,
              Helvetica,
              sans-serif;

            font-size: 10px;

            -webkit-print-color-adjust:
              exact;

            print-color-adjust:
              exact;
          }

          .print-header {
            display: flex;

            align-items:
              flex-end;

            justify-content:
              space-between;

            gap: 20px;

            padding-bottom: 9px;

            margin-bottom: 9px;

            border-bottom:
              2px solid #353a43;
          }

          .print-header h1 {
            margin: 0;

            font-size: 19px;
          }

          .print-header p {
            margin:
              4px 0 0;

            color: #737985;

            font-size: 9px;
          }

          .print-date {
            color: #666;

            font-size: 8px;

            text-align: right;
          }

          .meta-grid {
            display: grid;

            grid-template-columns:
              repeat(5, 1fr);

            gap: 5px;

            margin-bottom: 8px;
          }

          .meta-grid > div {
            padding: 6px;

            border:
              1px solid #d3d6dc;

            border-radius: 3px;
          }

          .meta-grid span {
            display: block;

            margin-bottom: 2px;

            color: #777d87;

            font-size: 7px;

            text-transform:
              uppercase;
          }

          .meta-grid strong {
            font-size: 8px;
          }

          .summary-grid {
            display: grid;

            grid-template-columns:
              repeat(6, 1fr);

            gap: 5px;

            margin-bottom: 9px;
          }

          .summary-grid div {
            padding: 6px;

            border:
              1px solid #d3d6dc;

            text-align: center;
          }

          .summary-grid span {
            display: block;

            color: #737985;

            font-size: 7px;
          }

          .summary-grid strong {
            display: block;

            margin-top: 2px;

            font-size: 14px;
          }

          table {
            width: 100%;

            border-collapse:
              collapse;
          }

          thead {
            display:
              table-header-group;
          }

          tr {
            page-break-inside:
              avoid;
          }

          th,
          td {
            padding: 4px;

            border:
              1px solid #aaa;

            font-size: 7.5px;

            vertical-align:
              middle;
          }

          th {
            background: #eceef1;

            font-weight: 700;

            text-align: center;
          }

          td {
            text-align: center;
          }

          .student {
            text-align: left;

            min-width: 105px;
          }

          .student strong {
            display: block;
          }

          .student small {
            display: block;

            margin-top: 2px;

            color: #777;
          }

          .batch {
            text-align: left;

            min-width: 95px;
          }

          .present {
            color: #137844;
            font-weight: 700;
          }

          .absent {
            color: #bf3032;
            font-weight: 700;
          }

          .pending {
            color: #a76613;
            font-weight: 700;
          }

          .percentage.good {
            color: #137844;
            font-weight: 700;
          }

          .percentage.low {
            color: #bf3032;
            font-weight: 700;
          }

          .low-row {
            background: #fff4f4;
          }

          .footer {
            display: flex;

            justify-content:
              space-between;

            gap: 15px;

            margin-top: 15px;

            padding-top: 8px;

            border-top:
              1px solid #bbb;

            color: #666;

            font-size: 7px;
          }

        </style>

      </head>

      <body>

        ${content}

        <script>

          window.onload =
            function () {

              setTimeout(
                function () {
                  window.focus();
                  window.print();
                },
                350
              );

            };

          window.onafterprint =
            function () {
              window.close();
            };

        </script>

      </body>

      </html>
    `);

    printWindow.document.close();
  };

  const printAnnualRegister = () => {
    setPrintMenuOpen(false);

    if (!hasBatchSelection) {
      SweetAlertShow(
        "info",
        "Select Batch",
        "Please select a batch or All Batches first.",
      );

      return;
    }

    if (!filteredRows.length) {
      SweetAlertShow(
        "info",
        "No Data",
        "There are no students available to print.",
      );

      return;
    }

    const rowsHtml = filteredRows
      .map((row, index) => {
        const student = row.student;

        const course = student?.latest_course_and_fee?.course?.title || "-";

        const batch = student?.latest_course_and_fee?.batch?.title || "-";

        return `
                <tr
                  class="${
                    row.workingDays > 0 && row.percentage < 75 ? "low-row" : ""
                  }"
                >

                  <td>
                    ${index + 1}
                  </td>

                  <td class="student">

                    <strong>
                      ${escapeHtml(student?.name || "-")}
                    </strong>

                    <small>
                      ${escapeHtml(student?.gender || "")}
                    </small>

                  </td>

                  <td>

                    ${escapeHtml(student?.registration_no || "-")}

                    <br />

                    <small>
                      ${escapeHtml(student?.phone || student?.mobile || "")}
                    </small>

                  </td>

                  <td class="batch">

                    <strong>
                      ${escapeHtml(batch)}
                    </strong>

                    <br />

                    <small>
                      ${escapeHtml(course)}
                    </small>

                  </td>

                  <td>
                    ${escapeHtml(formatReadableDate(row.admissionDate))}
                  </td>

                  <td>
                    ${row.totalDays}
                  </td>

                  <td>
                    ${row.holidays}
                  </td>

                  <td>
                    ${row.workingDays}
                  </td>

                  <td class="present">
                    ${row.present}
                  </td>

                  <td class="absent">
                    ${row.absent}
                  </td>

                  <td class="pending">
                    ${row.notMarked}
                  </td>

                  <td
                    class="percentage ${row.percentage >= 75 ? "good" : "low"}"
                  >
                    ${row.percentage}%
                  </td>

                </tr>
              `;
      })
      .join("");

    const html = `

        <div class="print-header">

          <div>

            <h1>
              Student Annual Attendance Register
            </h1>

            <p>
              Academic Session:
              ${escapeHtml(selectedSession?.session || "-")}
            </p>

          </div>

          <div class="print-date">
            Printed:
            ${escapeHtml(new Date().toLocaleString("en-IN"))}
          </div>

        </div>

        ${getPrintMetaHtml()}

        <div class="summary-grid">

          <div>
            <span>
              Students
            </span>

            <strong>
              ${summary.students}
            </strong>
          </div>

          <div>
            <span>
              Present
            </span>

            <strong>
              ${summary.present}
            </strong>
          </div>

          <div>
            <span>
              Absent
            </span>

            <strong>
              ${summary.absent}
            </strong>
          </div>

          <div>
            <span>
              Not Marked
            </span>

            <strong>
              ${summary.notMarked}
            </strong>
          </div>

          <div>
            <span>
              Below 75%
            </span>

            <strong>
              ${summary.lowAttendance}
            </strong>
          </div>

          <div>
            <span>
              Attendance
            </span>

            <strong>
              ${summary.percentage}%
            </strong>
          </div>

        </div>

        <table>

          <thead>

            <tr>

              <th>#</th>

              <th>
                Student
              </th>

              <th>
                Reg. No. / Mobile
              </th>

              <th>
                Batch / Course
              </th>

              <th>
                Admission
              </th>

              <th>
                Total Days
              </th>

              <th>
                Holidays
              </th>

              <th>
                Working Days
              </th>

              <th>
                Present
              </th>

              <th>
                Absent
              </th>

              <th>
                Not Marked
              </th>

              <th>
                Attendance %
              </th>

            </tr>

          </thead>

          <tbody>
            ${rowsHtml}
          </tbody>

        </table>

        <div class="footer">

          <span>
            Total Students:
            ${filteredRows.length}
          </span>

          <span>
            Class Teacher Signature
            ____________________

            &nbsp;&nbsp;&nbsp;&nbsp;

            Principal Signature
            ____________________
          </span>

        </div>
      `;

    openPrintWindow("Annual Attendance Register", html);
  };

  /* =======================================================
     PRINT SUMMARY
  ======================================================= */

  const printAnnualSummary = () => {
    setPrintMenuOpen(false);

    if (!hasBatchSelection) {
      SweetAlertShow(
        "info",
        "Select Batch",
        "Please select a batch or All Batches first.",
      );

      return;
    }

    if (!filteredRows.length) {
      SweetAlertShow(
        "info",
        "No Data",
        "There are no students available to print.",
      );

      return;
    }

    const rowsHtml = filteredRows
      .map(
        (row, index) => `
              <tr>

                <td>
                  ${index + 1}
                </td>

                <td class="student">

                  <strong>
                    ${escapeHtml(row.student?.name || "-")}
                  </strong>

                  <small>
                    ${escapeHtml(row.student?.registration_no || "")}
                  </small>

                </td>

                <td>
                  ${row.workingDays}
                </td>

                <td class="present">
                  ${row.present}
                </td>

                <td class="absent">
                  ${row.absent}
                </td>

                <td class="pending">
                  ${row.notMarked}
                </td>

                <td
                  class="percentage ${row.percentage >= 75 ? "good" : "low"}"
                >
                  ${row.percentage}%
                </td>

              </tr>
            `,
      )
      .join("");

    const html = `

        <div class="print-header">

          <div>

            <h1>
              Annual Attendance Summary
            </h1>

            <p>
              Academic Session:
              ${escapeHtml(selectedSession?.session || "-")}
            </p>

          </div>

        </div>

        ${getPrintMetaHtml()}

        <div class="summary-grid">

          <div>
            <span>Students</span>
            <strong>
              ${summary.students}
            </strong>
          </div>

          <div>
            <span>Present</span>
            <strong>
              ${summary.present}
            </strong>
          </div>

          <div>
            <span>Absent</span>
            <strong>
              ${summary.absent}
            </strong>
          </div>

          <div>
            <span>Not Marked</span>
            <strong>
              ${summary.notMarked}
            </strong>
          </div>

          <div>
            <span>Below 75%</span>
            <strong>
              ${summary.lowAttendance}
            </strong>
          </div>

          <div>
            <span>Overall</span>
            <strong>
              ${summary.percentage}%
            </strong>
          </div>

        </div>

        <table>

          <thead>

            <tr>

              <th>#</th>

              <th>
                Student
              </th>

              <th>
                Working Days
              </th>

              <th>
                Present
              </th>

              <th>
                Absent
              </th>

              <th>
                Not Marked
              </th>

              <th>
                Attendance %
              </th>

            </tr>

          </thead>

          <tbody>
            ${rowsHtml}
          </tbody>

        </table>
      `;

    openPrintWindow("Annual Attendance Summary", html);
  };

  /* =======================================================
     EXCEL EXPORT
  ======================================================= */

  const exportExcel = () => {
    if (!hasBatchSelection) {
      SweetAlertShow(
        "info",
        "Select Batch",
        "Please select a batch or All Batches first.",
      );

      return;
    }

    if (!filteredRows.length) {
      SweetAlertShow(
        "info",
        "No Data",
        "There are no students available to export.",
      );

      return;
    }

    const headers = [
      "#",
      "Student Name",
      "Gender",
      "Mobile",
      "Registration No",
      "Course",
      "Batch",
      "Admission Date",
      "Report Start",
      "Report End",
      "Total Days",
      "Holidays",
      "Working Days",
      "Present",
      "Absent",
      "Not Marked",
      "Attendance %",
    ];

    const excelRows = filteredRows.map((row, index) => {
      const student = row.student;

      const course = student?.latest_course_and_fee?.course?.title || "";

      const batch = student?.latest_course_and_fee?.batch?.title || "";

      return [
        index + 1,

        student?.name || "",

        student?.gender || "",

        student?.phone || student?.mobile || "",

        student?.registration_no || "",

        course,

        batch,

        formatReadableDate(row.admissionDate),

        formatReadableDate(row.reportStartDate),

        formatReadableDate(row.reportEndDate),

        row.totalDays,

        row.holidays,

        row.workingDays,

        row.present,

        row.absent,

        row.notMarked,

        `${row.percentage}%`,
      ];
    });

    const html = `
      <html
        xmlns:o="urn:schemas-microsoft-com:office:office"
        xmlns:x="urn:schemas-microsoft-com:office:excel"
        xmlns="http://www.w3.org/TR/REC-html40"
      >

      <head>
        <meta charset="UTF-8">
      </head>

      <body>

        <table border="1">

          <tr>

            <th
              colspan="${headers.length}"
              style="
                font-size:18px;
                font-weight:bold;
                text-align:center;
              "
            >
              Student Annual Attendance Register
            </th>

          </tr>

          <tr>

            <td
              colspan="${headers.length}"
            >

              Session:
              ${escapeHtml(selectedSession?.session || "-")}

              | Course:
              ${escapeHtml(selectedCourse?.title || "All Courses")}

              | Batch:
              ${escapeHtml(
                isAllBatches ? "All Batches" : selectedBatch?.title || "-",
              )}

              | Teacher:
              ${escapeHtml(selectedStaff?.name || "All Teachers")}

              | Student Type:
              ${escapeHtml(
                filters.student_type === "hostel"
                  ? "Hostel Students"
                  : "All Students",
              )}

            </td>

          </tr>

          <tr>

            ${headers
              .map((header) => `<th>${escapeHtml(header)}</th>`)
              .join("")}

          </tr>

          ${excelRows
            .map(
              (row) => `
                <tr>

                  ${row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("")}

                </tr>
              `,
            )
            .join("")}

        </table>

      </body>

      </html>
    `;

    const sessionName = String(selectedSession?.session || "annual").replace(
      /[^a-zA-Z0-9-_]/g,
      "-",
    );

    downloadBlob(
      html,

      `annual-attendance-${sessionName}.xls`,

      "application/vnd.ms-excel;charset=utf-8;",
    );
  };

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar annual-layout">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y annual-container">
                <MasterV2 />

                {/* =========================================
                    PAGE HEADER
                ========================================= */}

                {/* <section className="annual-header">
                  <div className="annual-header-left">
                    <div className="annual-title-icon">
                      <i className="ri-calendar-check-line" />
                    </div>

                    <div>
                      <div className="annual-breadcrumb">
                        Attendance Reports
                        <i className="ri-arrow-right-s-line" />
                        Annual Register
                      </div>

                      <h1>Annual Attendance Register</h1>

                      <p>
                        Complete academic-session attendance overview for
                        students, batches and classes.
                      </p>
                    </div>
                  </div>

                  <div className="annual-header-actions">
                    <button
                      type="button"
                      className="annual-btn refresh"
                      disabled={!hasBatchSelection}
                      onClick={getAttendanceList}
                    >
                      <i className="ri-refresh-line" />
                      Refresh
                    </button>

                    <button
                      type="button"
                      className="annual-btn excel"
                      disabled={!hasBatchSelection}
                      onClick={exportExcel}
                    >
                      <i className="ri-file-excel-2-line" />
                      Export Excel
                    </button>

                    <div className="annual-print-wrapper">
                      <button
                        type="button"
                        className="annual-btn print"
                        disabled={!hasBatchSelection}
                        onClick={() =>
                          setPrintMenuOpen((previous) => !previous)
                        }
                      >
                        <i className="ri-printer-line" />
                        Print
                        <i className="ri-arrow-down-s-line" />
                      </button>

                      {printMenuOpen && (
                        <div className="annual-print-menu">
                          <button type="button" onClick={printAnnualRegister}>
                            <span className="annual-print-icon">
                              <i className="ri-table-line" />
                            </span>

                            <span>
                              <strong>Annual Register</strong>

                              <small>Complete annual student attendance</small>
                            </span>
                          </button>

                          <button type="button" onClick={printAnnualSummary}>
                            <span className="annual-print-icon green">
                              <i className="ri-bar-chart-box-line" />
                            </span>

                            <span>
                              <strong>Attendance Summary</strong>

                              <small>Compact annual attendance summary</small>
                            </span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </section> */}

                {/* =========================================
                    FILTER CARD
                ========================================= */}

                <section className="annual-filter-card mt-5">
                  <div className="annual-filter-heading">
                    <div>
                      <h2>
                        <i className="ri-filter-3-line" />
                        Annual Attendance Register
                      </h2>

                      <p>
                        Select a batch or All Batches to generate the annual
                        attendance report.
                      </p>
                    </div>

                    <div className="annual-header-actions">
                      <button
                        type="button"
                        className="annual-reset"
                        onClick={resetFilters}
                      >
                        <i className="ri-restart-line" />
                        Reset
                      </button>
                      <button
                        type="button"
                        className="annual-btn refresh"
                        disabled={!hasBatchSelection}
                        onClick={getAttendanceList}
                      >
                        <i className="ri-refresh-line" />
                        Refresh
                      </button>

                      <button
                        type="button"
                        className="annual-btn excel"
                        disabled={!hasBatchSelection}
                        onClick={exportExcel}
                      >
                        <i className="ri-file-excel-2-line" />
                        Export Excel
                      </button>

                      <div className="annual-print-wrapper">
                        <button
                          type="button"
                          className="annual-btn print"
                          disabled={!hasBatchSelection}
                          onClick={() =>
                            setPrintMenuOpen((previous) => !previous)
                          }
                        >
                          <i className="ri-printer-line" />
                          Print
                          <i className="ri-arrow-down-s-line" />
                        </button>

                        {printMenuOpen && (
                          <div className="annual-print-menu">
                            <button type="button" onClick={printAnnualRegister}>
                              <span className="annual-print-icon">
                                <i className="ri-table-line" />
                              </span>

                              <span>
                                <strong>Annual Register</strong>

                                <small>
                                  Complete annual student attendance
                                </small>
                              </span>
                            </button>

                            <button type="button" onClick={printAnnualSummary}>
                              <span className="annual-print-icon green">
                                <i className="ri-bar-chart-box-line" />
                              </span>

                              <span>
                                <strong>Attendance Summary</strong>

                                <small>Compact annual attendance summary</small>
                              </span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="annual-filter-grid">
                    {/* SESSION */}

                    <div className="annual-field">
                      <label>Academic Session</label>

                      <div className="annual-control">
                        <i className="ri-calendar-event-line" />

                        <select
                          value={filters.session_id}
                          onChange={handleSessionChange}
                        >
                          <option value="">Select Session</option>

                          {sessions.map((session) => (
                            <option key={session.id} value={session.id}>
                              {session.session}

                              {Number(session.status) === 1 ? " - Active" : ""}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* COURSE */}

                    <div className="annual-field">
                      <label>Course</label>

                      <div className="annual-control">
                        <i className="ri-book-open-line" />

                        <select
                          value={filters.course_id}
                          onChange={handleCourseChange}
                        >
                          <option value="">All Courses</option>

                          {courses.map((course) => (
                            <option key={course.id} value={course.id}>
                              {course.title}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* BATCH */}

                    <div className="annual-field batch-field">
                      <label>
                        Batch
                        <span className="required-star">*</span>
                      </label>

                      <div className="annual-control">
                        <i className="ri-group-2-line" />

                        <select
                          value={filters.batch_id}
                          onChange={handleBatchChange}
                        >
                          <option value="" disabled>
                            Select Batch
                          </option>

                          <option value={ALL_BATCHES}>All Batches</option>

                          {batches.map((batch) => (
                            <option key={batch.id} value={batch.id}>
                              {batch.title}

                              {batch?.course?.title
                                ? ` - ${batch.course.title}`
                                : ""}
                            </option>
                          ))}
                        </select>
                      </div>

                      {!hasBatchSelection && (
                        <small className="annual-batch-help">
                          Select batch to load annual report
                        </small>
                      )}
                    </div>

                    {/* STUDENT TYPE */}

                    <div className="annual-field">
                      <label>Student Type</label>

                      <div className="annual-control">
                        <i className="ri-user-settings-line" />

                        <select
                          value={filters.student_type}
                          onChange={(event) => {
                            setData([]);

                            setFilters((previous) => ({
                              ...previous,

                              student_type: event.target.value,
                            }));
                          }}
                        >
                          <option value="">All Students</option>

                          <option value="hostel">Hostel Students</option>
                        </select>
                      </div>
                    </div>

                    {/* TEACHER */}

                    <div className="annual-field">
                      <label>Class Teacher</label>

                      <div className="annual-control">
                        <i className="ri-user-star-line" />

                        <select
                          value={filters.staff_id}
                          onChange={(event) => {
                            setData([]);

                            setFilters((previous) => ({
                              ...previous,

                              staff_id: event.target.value,
                            }));
                          }}
                        >
                          <option value="">All Teachers</option>

                          {staffs.map((staff) => (
                            <option key={staff.id} value={staff.id}>
                              {staff.name}

                              {staff.registration_no
                                ? ` (${staff.registration_no})`
                                : ""}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {filterLoading && (
                    <div className="annual-filter-loading">
                      <i className="ri-loader-4-line annual-spin" />
                      Loading filter options...
                    </div>
                  )}
                </section>

                {/* =========================================
                    DEFAULT / NO BATCH
                ========================================= */}

                {!hasBatchSelection && (
                  <section className="annual-empty-selection">
                    <div className="annual-empty-icon">
                      <i className="ri-calendar-todo-line" />
                    </div>

                    <h2>Select Batch to View Annual Attendance</h2>

                    <p>
                      Select a specific batch from the filter above or choose
                      <strong> All Batches </strong>
                      to generate the complete academic-session attendance
                      report.
                    </p>

                    <div className="annual-empty-info">
                      {selectedSession && (
                        <span>
                          <i className="ri-calendar-event-line" />

                          {selectedSession.session}
                        </span>
                      )}

                      {selectedCourse && (
                        <span>
                          <i className="ri-book-open-line" />

                          {selectedCourse.title}
                        </span>
                      )}
                    </div>

                    <div className="annual-empty-hint">
                      <i className="ri-arrow-up-line" />
                      Choose Batch from Report Filters
                    </div>
                  </section>
                )}

                {/* =========================================
                    REPORT CONTENT
                ========================================= */}

                {hasBatchSelection && (
                  <>
                    {/* REPORT INFO */}

                    <section className="annual-report-info">
                      <div className="annual-session-info">
                        <span className="annual-session-icon">
                          <i className="ri-calendar-check-line" />
                        </span>

                        <div>
                          <small>Academic Session</small>

                          <strong>{selectedSession?.session || "-"}</strong>
                        </div>
                      </div>

                      <div className="annual-report-tags">
                        <span>
                          <i className="ri-book-open-line" />

                          {selectedCourse?.title || "All Courses"}
                        </span>

                        <span className="highlight">
                          <i className="ri-group-line" />

                          {isAllBatches
                            ? "All Batches"
                            : selectedBatch?.title || "Batch"}
                        </span>

                        {selectedStaff && (
                          <span>
                            <i className="ri-user-star-line" />

                            {selectedStaff.name}
                          </span>
                        )}

                        {filters.student_type === "hostel" && (
                          <span>
                            <i className="ri-hotel-bed-line" />
                            Hostel Students
                          </span>
                        )}
                      </div>
                    </section>

                    {/* SUMMARY */}

                    <section className="annual-summary-grid">
                      <button
                        type="button"
                        className={`annual-summary-card ${
                          attendanceView === "all" ? "selected" : ""
                        }`}
                        onClick={() => setAttendanceView("all")}
                      >
                        <div className="annual-summary-top">
                          <span className="summary-icon purple">
                            <i className="ri-group-line" />
                          </span>

                          <small>Students</small>
                        </div>

                        <strong>{summary.students}</strong>

                        <p>Students in this report</p>
                      </button>

                      <div className="annual-summary-card">
                        <div className="annual-summary-top">
                          <span className="summary-icon green">
                            <i className="ri-checkbox-circle-line" />
                          </span>

                          <small>Present</small>
                        </div>

                        <strong>{summary.present}</strong>

                        <p>Total present attendance</p>
                      </div>

                      <button
                        type="button"
                        className={`annual-summary-card ${
                          attendanceView === "absent" ? "selected red" : ""
                        }`}
                        onClick={() =>
                          setAttendanceView(
                            attendanceView === "absent" ? "all" : "absent",
                          )
                        }
                      >
                        <div className="annual-summary-top">
                          <span className="summary-icon red">
                            <i className="ri-close-circle-line" />
                          </span>

                          <small>Absent</small>
                        </div>

                        <strong>{summary.absent}</strong>

                        <p>Total absent attendance</p>
                      </button>

                      <button
                        type="button"
                        className={`annual-summary-card ${
                          attendanceView === "not_marked"
                            ? "selected orange"
                            : ""
                        }`}
                        onClick={() =>
                          setAttendanceView(
                            attendanceView === "not_marked"
                              ? "all"
                              : "not_marked",
                          )
                        }
                      >
                        <div className="annual-summary-top">
                          <span className="summary-icon orange">
                            <i className="ri-time-line" />
                          </span>

                          <small>Not Marked</small>
                        </div>

                        <strong>{summary.notMarked}</strong>

                        <p>Attendance not marked</p>
                      </button>

                      <button
                        type="button"
                        className={`annual-summary-card ${
                          attendanceView === "low" ? "selected red" : ""
                        }`}
                        onClick={() =>
                          setAttendanceView(
                            attendanceView === "low" ? "all" : "low",
                          )
                        }
                      >
                        <div className="annual-summary-top">
                          <span className="summary-icon red">
                            <i className="ri-error-warning-line" />
                          </span>

                          <small>Below 75%</small>
                        </div>

                        <strong>{summary.lowAttendance}</strong>

                        <p>Low attendance students</p>
                      </button>

                      <div className="annual-summary-card">
                        <div className="annual-summary-top">
                          <span className="summary-icon blue">
                            <i className="ri-pie-chart-line" />
                          </span>

                          <small>Overall</small>
                        </div>

                        <strong>{summary.percentage}%</strong>

                        <p>Overall attendance rate</p>
                      </div>
                    </section>

                    {/* =====================================
                        TABLE
                    ===================================== */}

                    <section className="annual-register-card">
                      <div className="annual-table-header">
                        <div>
                          <h2>Student Annual Attendance</h2>

                          <p>
                            Showing <strong>{filteredRows.length}</strong>{" "}
                            student
                            {filteredRows.length === 1 ? "" : "s"}
                            {" • "}
                            <strong>
                              {isAllBatches
                                ? "All Batches"
                                : selectedBatch?.title || ""}
                            </strong>
                          </p>
                        </div>

                        <div className="annual-table-tools">
                          <div className="annual-view-filter">
                            <i className="ri-filter-line" />

                            <select
                              value={attendanceView}
                              onChange={(event) =>
                                setAttendanceView(event.target.value)
                              }
                            >
                              <option value="all">All Students</option>

                              <option value="low">Below 75%</option>

                              <option value="good">75% & Above</option>

                              <option value="excellent">90% & Above</option>

                              <option value="absent">Has Absence</option>

                              <option value="not_marked">Has Not Marked</option>
                            </select>
                          </div>

                          <div className="annual-search">
                            <i className="ri-search-line" />

                            <input
                              type="text"
                              value={search}
                              placeholder="Search student, reg. no., batch..."
                              onChange={(event) =>
                                setSearch(event.target.value)
                              }
                            />

                            {search && (
                              <button
                                type="button"
                                onClick={() => setSearch("")}
                              >
                                <i className="ri-close-line" />
                              </button>
                            )}
                          </div>

                          <button
                            type="button"
                            className="annual-table-excel"
                            onClick={exportExcel}
                          >
                            <i className="ri-file-excel-2-line" />
                            Excel
                          </button>
                        </div>
                      </div>

                      <div className="annual-table-scroll">
                        <table className="annual-table">
                          <thead>
                            <tr>
                              <th className="serial">#</th>

                              <th className="student">Student</th>

                              <th>Registration / Mobile</th>

                              <th>Batch & Course</th>

                              <th>Admission Date</th>

                              <th>Total Days</th>

                              <th>Holidays</th>

                              <th>Working Days</th>

                              <th>Present</th>

                              <th>Absent</th>

                              <th>Not Marked</th>

                              <th>Attendance</th>
                            </tr>
                          </thead>

                          <tbody>
                            {!loading && filteredRows.length === 0 && (
                              <tr>
                                <td colSpan="12" className="annual-no-data">
                                  <i className="ri-calendar-close-line" />

                                  <strong>No Attendance Data Found</strong>

                                  <span>
                                    No student attendance records were found for
                                    the selected filters.
                                  </span>
                                </td>
                              </tr>
                            )}

                            {filteredRows.map((row, index) => {
                              const student = row.student;

                              const course =
                                student?.latest_course_and_fee?.course?.title ||
                                "-";

                              const batch =
                                student?.latest_course_and_fee?.batch?.title ||
                                "-";

                              const low =
                                row.workingDays > 0 && row.percentage < 75;

                              return (
                                <tr
                                  key={student?.id || index}
                                  className={low ? "annual-low-row" : ""}
                                >
                                  <td className="serial">{index + 1}</td>

                                  {/* STUDENT */}

                                  <td className="student">
                                    <div className="annual-student">
                                      <div className="annual-avatar">
                                        {getInitials(student?.name)}
                                      </div>

                                      <div>
                                        <strong>{student?.name || "-"}</strong>

                                        <span>
                                          {student?.gender || "Student"}
                                        </span>
                                      </div>
                                    </div>
                                  </td>

                                  {/* REG */}

                                  <td>
                                    <div className="annual-two-line">
                                      <strong>
                                        {student?.registration_no || "-"}
                                      </strong>

                                      <span>
                                        {student?.phone ||
                                          student?.mobile ||
                                          "-"}
                                      </span>
                                    </div>
                                  </td>

                                  {/* COURSE */}

                                  <td>
                                    <div className="annual-two-line">
                                      <strong>{batch}</strong>

                                      <span>{course}</span>
                                    </div>
                                  </td>

                                  {/* ADMISSION */}

                                  <td>
                                    <span className="annual-date">
                                      {formatReadableDate(row.admissionDate)}
                                    </span>
                                  </td>

                                  {/* TOTAL */}

                                  <td>
                                    <span className="annual-number neutral">
                                      {row.totalDays}
                                    </span>
                                  </td>

                                  {/* HOLIDAY */}

                                  <td>
                                    <span className="annual-number holiday">
                                      {row.holidays}
                                    </span>
                                  </td>

                                  {/* WORKING */}

                                  <td>
                                    <span className="annual-number blue">
                                      {row.workingDays}
                                    </span>
                                  </td>

                                  {/* PRESENT */}

                                  <td>
                                    <span className="annual-number green">
                                      {row.present}
                                    </span>
                                  </td>

                                  {/* ABSENT */}

                                  <td>
                                    <span className="annual-number red">
                                      {row.absent}
                                    </span>
                                  </td>

                                  {/* NOT MARKED */}

                                  <td>
                                    <span className="annual-number orange">
                                      {row.notMarked}
                                    </span>
                                  </td>

                                  {/* PERCENTAGE */}

                                  <td>
                                    <div
                                      className={`annual-percentage ${
                                        row.percentage >= 90
                                          ? "excellent"
                                          : row.percentage >= 75
                                            ? "good"
                                            : "low"
                                      }`}
                                    >
                                      {row.percentage}%
                                    </div>

                                    <div className="annual-progress">
                                      <span
                                        style={{
                                          width: `${Math.min(
                                            row.percentage,
                                            100,
                                          )}%`,
                                        }}
                                      />
                                    </div>

                                    {low && (
                                      <small className="annual-low-text">
                                        Below 75%
                                      </small>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      <div className="annual-table-footer">
                        <span>
                          <i className="ri-information-line" />
                          Annual attendance is calculated from admission/session
                          start through the report end date.
                        </span>

                        <strong>{selectedSession?.session || ""}</strong>
                      </div>
                    </section>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ===================================================
          STYLES
      =================================================== */}

      <style>{`

        .annual-layout {
          min-height: 100vh;
          background: #f6f7fb;
        }

        .annual-container {
          padding-top: 24px !important;
          padding-bottom: 40px !important;
        }

        /* ================================================
           HEADER
        ================================================ */

        .annual-header {
          display: flex;
          align-items: center;
          justify-content: space-between;

          gap: 20px;

          padding: 21px;

          margin-bottom: 16px;

          background: #fff;

          border: 1px solid #e3e6ec;
          border-radius: 15px;

          box-shadow:
            0 4px 20px
            rgba(40,45,70,.04);
        }

        .annual-header-left {
          display: flex;
          align-items: center;

          gap: 13px;

          min-width: 0;
        }

        .annual-title-icon {
          width: 53px;
          height: 53px;

          display: flex;
          align-items: center;
          justify-content: center;

          flex: 0 0 53px;

          border-radius: 13px;

          background: #eef0ff;
          color: #696cff;

          font-size: 26px;
        }

        .annual-breadcrumb {
          display: flex;
          align-items: center;

          gap: 3px;

          margin-bottom: 4px;

          color: #9297a2;

          font-size: 13px;
          font-weight: 600;
        }

        .annual-header h1 {
          margin: 0;

          color: #373c46;

          font-size: 27px;
          font-weight: 700;

          line-height: 1.2;
        }

        .annual-header p {
          margin: 6px 0 0;

          color: #898f9a;

          font-size: 14px;

          line-height: 1.5;
        }

        .annual-header-actions {
          display: flex;
          align-items: center;

          gap: 8px;

          flex-shrink: 0;
        }

        .annual-btn {
          min-height: 43px;

          display: inline-flex;
          align-items: center;
          justify-content: center;

          gap: 7px;

          padding: 9px 14px;

          border-radius: 9px;

          font-size: 13px;
          font-weight: 700;

          white-space: nowrap;

          cursor: pointer;
        }

        .annual-btn:disabled {
          opacity: .48;
          cursor: not-allowed;
        }

        .annual-btn.refresh {
          border: 1px solid #dfe2e9;

          background: #fff;
          color: #5f646e;
        }

        .annual-btn.excel {
          border: 1px solid #d4eadc;

          background: #eef9f2;
          color: #268551;
        }

        .annual-btn.print {
          border: 1px solid #696cff;

          background: #696cff;
          color: #fff;
        }

        /* ================================================
           PRINT MENU
        ================================================ */

        .annual-print-wrapper {
          position: relative;
        }

        .annual-print-menu {
          position: absolute;

          top: calc(100% + 7px);
          right: 0;

          z-index: 1000;

          width: 290px;

          padding: 7px;

          border: 1px solid #e0e3e9;
          border-radius: 11px;

          background: #fff;

          box-shadow:
            0 15px 40px
            rgba(30,35,55,.15);
        }

        .annual-print-menu button {
          width: 100%;

          display: flex;
          align-items: center;

          gap: 10px;

          padding: 10px;

          border: 0;
          border-radius: 8px;

          background: transparent;
          color: #4d525d;

          text-align: left;

          cursor: pointer;
        }

        .annual-print-menu button:hover {
          background: #f7f8fb;
        }

        .annual-print-icon {
          width: 39px;
          height: 39px;

          display: flex;
          align-items: center;
          justify-content: center;

          flex: 0 0 39px;

          border-radius: 9px;

          background: #eef0ff;
          color: #696cff;

          font-size: 19px;
        }

        .annual-print-icon.green {
          background: #eaf8ef;
          color: #278653;
        }

        .annual-print-menu
        button > span:last-child {
          display: flex;
          flex-direction: column;
        }

        .annual-print-menu strong {
          font-size: 13px;
        }

        .annual-print-menu small {
          margin-top: 2px;

          color: #969ba6;

          font-size: 11px;
        }

        /* ================================================
           FILTERS
        ================================================ */

        .annual-filter-card {
          padding: 19px;

          margin-bottom: 15px;

          background: #fff;

          border: 1px solid #e2e5ec;
          border-radius: 14px;
        }

        .annual-filter-heading {
          display: flex;
          align-items: center;
          justify-content: space-between;

          gap: 15px;

          margin-bottom: 17px;
        }

        .annual-filter-heading h2 {
          display: flex;
          align-items: center;

          gap: 7px;

          margin: 0;

          color: #414650;

          font-size: 18px;
          font-weight: 700;
        }

        .annual-filter-heading h2 i {
          color: #696cff;
        }

        .annual-filter-heading p {
          margin: 5px 0 0;

          color: #9297a2;

          font-size: 13px;
        }

        .annual-reset {
          display: inline-flex;
          align-items: center;

          gap: 5px;

          padding: 8px 12px;

          border: 1px solid #dfe2e9;
          border-radius: 8px;

          background: #fff;
          color: #626772;

          font-size: 13px;
          font-weight: 700;

          cursor: pointer;
        }

        .annual-filter-grid {
          display: grid;

          grid-template-columns:
            repeat(
              5,
              minmax(0,1fr)
            );

          gap: 13px;
        }

        .annual-field label {
          display: block;

          margin-bottom: 7px;

          color: #555a64;

          font-size: 14px;
          font-weight: 700;
        }

        .required-star {
          margin-left: 3px;

          color: #d74647;
        }

        .annual-control {
          position: relative;
        }

        .annual-control > i {
          position: absolute;

          top: 50%;
          left: 12px;

          z-index: 2;

          transform:
            translateY(-50%);

          color: #9398a3;

          font-size: 17px;

          pointer-events: none;
        }

        .annual-control select {
          width: 100%;
          height: 47px;

          padding:
            8px 10px 8px 38px;

          border: 1px solid #dce0e8;
          border-radius: 9px;

          outline: none;

          background: #fff;
          color: #4c515b;

          font-size: 14px;
          font-weight: 500;
        }

        .annual-control select:focus {
          border-color: #8e91ff;

          box-shadow:
            0 0 0 3px
            rgba(105,108,255,.09);
        }

        .batch-field
        .annual-control select {
          border-color: #b8bbff;

          background: #fbfbff;
        }

        .annual-batch-help {
          display: block;

          margin-top: 5px;

          color: #696cff;

          font-size: 11px;
          font-weight: 600;
        }

        .annual-filter-loading {
          margin-top: 10px;

          color: #777c87;

          font-size: 12px;
        }

        /* ================================================
           DEFAULT EMPTY STATE
        ================================================ */

        .annual-empty-selection {
          min-height: 420px;

          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;

          padding: 45px 20px;

          border:
            1px dashed #cfd3e5;

          border-radius: 15px;

          background: #fff;

          text-align: center;
        }

        .annual-empty-icon {
          width: 78px;
          height: 78px;

          display: flex;
          align-items: center;
          justify-content: center;

          margin-bottom: 18px;

          border-radius: 50%;

          background: #eef0ff;
          color: #696cff;

          font-size: 35px;
        }

        .annual-empty-selection h2 {
          margin: 0;

          color: #40454f;

          font-size: 22px;
          font-weight: 700;
        }

        .annual-empty-selection > p {
          max-width: 620px;

          margin: 10px auto 0;

          color: #888e99;

          font-size: 14px;

          line-height: 1.7;
        }

        .annual-empty-selection
        > p strong {
          color: #696cff;
        }

        .annual-empty-info {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-wrap: wrap;

          gap: 7px;

          margin-top: 20px;
        }

        .annual-empty-info span {
          display: inline-flex;
          align-items: center;

          gap: 5px;

          padding: 7px 11px;

          border: 1px solid #e1e4eb;
          border-radius: 8px;

          background: #fafbfc;
          color: #646974;

          font-size: 12px;
          font-weight: 600;
        }

        .annual-empty-hint {
          display: inline-flex;
          align-items: center;

          gap: 5px;

          margin-top: 25px;

          color: #696cff;

          font-size: 12px;
          font-weight: 700;
        }

        /* ================================================
           REPORT INFO
        ================================================ */

        .annual-report-info {
          display: flex;
          align-items: center;
          justify-content: space-between;

          gap: 15px;

          padding: 12px 14px;

          margin-bottom: 14px;

          border: 1px solid #e0e3e9;
          border-radius: 11px;

          background: #fff;
        }

        .annual-session-info {
          display: flex;
          align-items: center;

          gap: 9px;
        }

        .annual-session-icon {
          width: 40px;
          height: 40px;

          display: flex;
          align-items: center;
          justify-content: center;

          border-radius: 9px;

          background: #eef0ff;
          color: #696cff;

          font-size: 19px;
        }

        .annual-session-info div {
          display: flex;
          flex-direction: column;
        }

        .annual-session-info small {
          color: #9297a2;

          font-size: 11px;
        }

        .annual-session-info strong {
          margin-top: 1px;

          color: #454a54;

          font-size: 15px;
        }

        .annual-report-tags {
          display: flex;
          justify-content: flex-end;
          flex-wrap: wrap;

          gap: 6px;
        }

        .annual-report-tags span {
          display: inline-flex;
          align-items: center;

          gap: 5px;

          padding: 6px 9px;

          border: 1px solid #e3e5eb;
          border-radius: 7px;

          color: #666b76;

          font-size: 11px;
          font-weight: 600;
        }

        .annual-report-tags
        .highlight {
          border-color: #d9dafe;

          background: #f7f7ff;
          color: #696cff;
        }

        /* ================================================
           SUMMARY
        ================================================ */

        .annual-summary-grid {
          display: grid;

          grid-template-columns:
            repeat(6,1fr);

          gap: 11px;

          margin-bottom: 14px;
        }

        .annual-summary-card {
          min-height: 135px;

          padding: 15px;

          border: 1px solid #e2e5eb;
          border-radius: 12px;

          background: #fff;
          color: inherit;

          text-align: left;
        }

        button.annual-summary-card {
          cursor: pointer;
        }

        button.annual-summary-card:hover {
          border-color: #cfd3dc;

          box-shadow:
            0 5px 15px
            rgba(30,35,55,.05);
        }

        .annual-summary-card.selected {
          border-color: #696cff;

          box-shadow:
            0 0 0 2px
            rgba(105,108,255,.08);
        }

        .annual-summary-card.selected.red {
          border-color: #d84a4b;

          box-shadow:
            0 0 0 2px
            rgba(216,74,75,.07);
        }

        .annual-summary-card.selected.orange {
          border-color: #d18a2b;
        }

        .annual-summary-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .summary-icon {
          width: 37px;
          height: 37px;

          display: flex;
          align-items: center;
          justify-content: center;

          border-radius: 9px;

          font-size: 18px;
        }

        .summary-icon.purple {
          background: #eef0ff;
          color: #696cff;
        }

        .summary-icon.green {
          background: #e8f8ef;
          color: #278e55;
        }

        .summary-icon.red {
          background: #ffeded;
          color: #d34849;
        }

        .summary-icon.orange {
          background: #fff3e2;
          color: #c67b19;
        }

        .summary-icon.blue {
          background: #eaf6ff;
          color: #3289c8;
        }

        .annual-summary-top small {
          color: #9297a2;

          font-size: 11px;
          font-weight: 700;

          text-transform: uppercase;
        }

        .annual-summary-card
        > strong {
          display: block;

          margin-top: 11px;

          color: #393e47;

          font-size: 28px;

          line-height: 1;
        }

        .annual-summary-card p {
          margin: 8px 0 0;

          color: #9398a3;

          font-size: 11px;

          line-height: 1.4;
        }

        /* ================================================
           REGISTER
        ================================================ */

        .annual-register-card {
          overflow: hidden;

          border: 1px solid #e1e4ea;
          border-radius: 14px;

          background: #fff;
        }

        .annual-table-header {
          display: flex;
          align-items: center;
          justify-content: space-between;

          gap: 15px;

          padding: 16px;

          border-bottom:
            1px solid #e9ebef;
        }

        .annual-table-header h2 {
          margin: 0;

          color: #414650;

          font-size: 18px;
          font-weight: 700;
        }

        .annual-table-header p {
          margin: 5px 0 0;

          color: #9297a2;

          font-size: 12px;
        }

        .annual-table-tools {
          display: flex;
          align-items: center;

          gap: 7px;
        }

        .annual-view-filter,
        .annual-search {
          position: relative;
        }

        .annual-view-filter > i,
        .annual-search > i {
          position: absolute;

          top: 50%;
          left: 10px;

          transform:
            translateY(-50%);

          color: #9499a4;

          font-size: 15px;

          pointer-events: none;
        }

        .annual-view-filter select {
          height: 41px;

          min-width: 150px;

          padding:
            7px 9px 7px 31px;

          border: 1px solid #dfe2e9;
          border-radius: 8px;

          background: #fff;
          color: #555a64;

          font-size: 12px;
        }

        .annual-search input {
          width: 265px;
          height: 41px;

          padding:
            7px 32px 7px 31px;

          border: 1px solid #dfe2e9;
          border-radius: 8px;

          outline: none;

          color: #50555f;

          font-size: 12px;
        }

        .annual-search input:focus {
          border-color: #8e91ff;
        }

        .annual-search button {
          position: absolute;

          top: 50%;
          right: 6px;

          transform:
            translateY(-50%);

          border: 0;

          background: transparent;
          color: #999;

          cursor: pointer;
        }

        .annual-table-excel {
          height: 41px;

          display: inline-flex;
          align-items: center;

          gap: 5px;

          padding: 7px 12px;

          border: 1px solid #d7e9de;
          border-radius: 8px;

          background: #effaf3;
          color: #278653;

          font-size: 12px;
          font-weight: 700;

          cursor: pointer;
        }

        /* ================================================
           TABLE
        ================================================ */

        .annual-table-scroll {
          width: 100%;

          overflow: auto;

          max-height: 68vh;
        }

        .annual-table {
          width: 100%;

          min-width: 1250px;

          border-collapse:
            separate;

          border-spacing: 0;
        }

        .annual-table th {
          position: sticky;

          top: 0;

          z-index: 15;

          padding: 12px 9px;

          border-right:
            1px solid #e1e4e9;

          border-bottom:
            1px solid #dfe2e8;

          background: #f5f6f8;
          color: #626772;

          font-size: 12px;
          font-weight: 700;

          text-align: center;

          white-space: nowrap;
        }

        .annual-table td {
          padding: 11px 9px;

          border-right:
            1px solid #eceef2;

          border-bottom:
            1px solid #eceef2;

          background: #fff;
          color: #555a64;

          font-size: 13px;

          text-align: center;
          vertical-align: middle;
        }

        .annual-table tbody
        tr:hover td {
          background: #fafbfc;
        }

        .annual-table
        .serial {
          width: 48px;
          min-width: 48px;
        }

        .annual-table
        .student {
          min-width: 190px;

          text-align: left;
        }

        .annual-student {
          display: flex;
          align-items: center;

          gap: 9px;
        }

        .annual-avatar {
          width: 37px;
          height: 37px;

          display: flex;
          align-items: center;
          justify-content: center;

          flex: 0 0 37px;

          border-radius: 50%;

          background: #eef0ff;
          color: #696cff;

          font-size: 11px;
          font-weight: 800;
        }

        .annual-student
        > div:last-child {
          min-width: 0;

          display: flex;
          flex-direction: column;
        }

        .annual-student strong {
          color: #414650;

          font-size: 13px;
        }

        .annual-student span {
          margin-top: 2px;

          color: #9297a2;

          font-size: 11px;
        }

        .annual-two-line {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;

          gap: 2px;
        }

        .annual-two-line strong {
          color: #4b505a;

          font-size: 12px;
        }

        .annual-two-line span {
          color: #9297a2;

          font-size: 10px;
        }

        .annual-date {
          color: #555a64;

          font-size: 12px;

          white-space: nowrap;
        }

        .annual-number {
          min-width: 38px;

          display: inline-flex;
          align-items: center;
          justify-content: center;

          padding: 5px 7px;

          border-radius: 6px;

          font-size: 12px;
          font-weight: 700;
        }

        .annual-number.neutral {
          background: #f1f2f4;
          color: #666b75;
        }

        .annual-number.holiday {
          background: #f0f1f3;
          color: #777c86;
        }

        .annual-number.blue {
          background: #eaf5ff;
          color: #3485bd;
        }

        .annual-number.green {
          background: #e8f8ef;
          color: #278e55;
        }

        .annual-number.red {
          background: #ffeded;
          color: #d34849;
        }

        .annual-number.orange {
          background: #fff3e2;
          color: #c57b1a;
        }

        .annual-percentage {
          display: inline-flex;
          align-items: center;
          justify-content: center;

          min-width: 64px;

          padding: 6px 8px;

          border-radius: 7px;

          font-size: 12px;
          font-weight: 800;
        }

        .annual-percentage.excellent {
          background: #e7f8ee;
          color: #23874f;
        }

        .annual-percentage.good {
          background: #eef0ff;
          color: #696cff;
        }

        .annual-percentage.low {
          background: #ffeded;
          color: #d34849;
        }

        .annual-progress {
          width: 65px;
          height: 4px;

          overflow: hidden;

          margin: 5px auto 0;

          border-radius: 10px;

          background: #eceef2;
        }

        .annual-progress span {
          display: block;

          height: 100%;

          border-radius: inherit;

          background: currentColor;
        }

        .annual-low-text {
          display: block;

          margin-top: 3px;

          color: #d34849;

          font-size: 9px;
          font-weight: 700;
        }

        .annual-low-row
        td:first-child {
          border-left:
            3px solid #dc5556;
        }

        .annual-no-data {
          height: 230px;

          text-align:
            center !important;
        }

        .annual-no-data i {
          display: block;

          margin-bottom: 8px;

          color: #9da2ac;

          font-size: 32px;
        }

        .annual-no-data strong {
          display: block;

          color: #555a64;

          font-size: 14px;
        }

        .annual-no-data span {
          display: block;

          margin-top: 5px;

          color: #959aa5;

          font-size: 11px;
        }

        .annual-table-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;

          gap: 10px;

          padding: 10px 14px;

          border-top:
            1px solid #e7e9ee;

          background: #fafbfc;
          color: #888e99;

          font-size: 11px;
        }

        .annual-table-footer span {
          display: flex;
          align-items: center;

          gap: 5px;
        }

        .annual-table-footer strong {
          color: #616671;
        }

        /* ================================================
           LOADER
        ================================================ */

        .annual-spin {
          display: inline-block;

          animation:
            annualSpin 1s
            linear infinite;
        }

        @keyframes annualSpin {
          to {
            transform:
              rotate(360deg);
          }
        }

        /* ================================================
           RESPONSIVE
        ================================================ */

        @media
        (max-width: 1450px) {

          .annual-filter-grid {
            grid-template-columns:
              repeat(3,1fr);
          }

          .annual-summary-grid {
            grid-template-columns:
              repeat(3,1fr);
          }

        }

        @media
        (max-width: 1100px) {

          .annual-header {
            align-items:
              flex-start;

            flex-direction:
              column;
          }

          .annual-header-actions {
            width: 100%;
          }

          .annual-report-info {
            align-items:
              flex-start;

            flex-direction:
              column;
          }

          .annual-report-tags {
            justify-content:
              flex-start;
          }

          .annual-table-header {
            align-items:
              flex-start;

            flex-direction:
              column;
          }

          .annual-table-tools {
            width: 100%;
          }

          .annual-search {
            flex: 1;
          }

          .annual-search input {
            width: 100%;
          }

        }

        @media
        (max-width: 767px) {

          .annual-container {
            padding-left:
              10px !important;

            padding-right:
              10px !important;
          }

          .annual-header {
            padding: 15px;
          }

          .annual-title-icon {
            display: none;
          }

          .annual-header h1 {
            font-size: 21px;
          }

          .annual-header p {
            font-size: 12px;
          }

          .annual-header-actions {
            display: grid;

            grid-template-columns:
              repeat(3,1fr);

            gap: 6px;
          }

          .annual-btn {
            width: 100%;

            padding: 8px 6px;

            font-size: 11px;
          }

          .annual-print-wrapper {
            width: 100%;
          }

          .annual-filter-card {
            padding: 14px;
          }

          .annual-filter-grid {
            grid-template-columns:
              1fr;
          }

          .annual-field label {
            font-size: 13px;
          }

          .annual-control select {
            height: 47px;

            font-size: 14px;
          }

          .annual-empty-selection {
            min-height: 330px;

            padding: 35px 15px;
          }

          .annual-empty-selection h2 {
            font-size: 19px;
          }

          .annual-empty-selection > p {
            font-size: 12px;
          }

          .annual-summary-grid {
            grid-template-columns:
              repeat(2,1fr);

            gap: 7px;
          }

          .annual-summary-card {
            min-height: 130px;

            padding: 12px;
          }

          .annual-summary-card
          > strong {
            font-size: 24px;
          }

          .annual-table-tools {
            align-items: stretch;

            flex-direction:
              column;
          }

          .annual-view-filter select,
          .annual-search input,
          .annual-table-excel {
            width: 100%;
            min-width: 0;
          }

          .annual-table-scroll {
            max-height: 70vh;
          }

        }

        @media
        (max-width: 430px) {

          .annual-header-actions {
            grid-template-columns:
              1fr 1fr;
          }

          .annual-summary-grid {
            grid-template-columns:
              repeat(2,1fr);
          }

          .annual-summary-top small {
            font-size: 9px;
          }

          .annual-summary-card p {
            font-size: 10px;
          }

        }

      `}</style>
    </>
  );
}
